import { isArray, isNull, toString } from 'lodash';
import { ApolloClient } from '@apollo/client';
import { getArticleTypeInGA } from '../../analytics/helpers/articleTypeHelper';
import { ADP } from '../../analytics/sourceTypes';
import { ProjectData } from '../../../lib/propertyTypes';
import {
  getMainCategory,
  getSubcategories,
  PostSubcategory,
} from '../domain/postCategories';
import logger from '../logger/logger';
import { getProjectNetProjectHref } from '../routing/urlGenerator';
import {
  fetchOtherPreviewPosts,
  fetchPostDetails,
  fetchReadNextPosts,
} from '../../../services/post';
import { fetchProjectArticles } from '../../../services/project';
import { fetchRecommendedVideos } from '../../../services/video';
import { fetchProjectData } from '../../../services/projectnetProject';
import { fetchDDListings } from '../../../services/ddListings';
import { fetchSeoMetaComposerData } from '../../../services/metaComposer';
import { prepareParamsForSeoMetaComposerRequest } from '../utility/prepareParams';
import {
  TransformedProjectNetDataType,
  transformProjectnetProjectData,
} from './transformProjectnetProjectData';
import { updateCurrentArticle } from '../../../store/content/slice';
import { updateCurrentProject } from '../../../store/project/slice';
import { updateCurrentSourcePage } from '../../../store/search/slice';
import { resolved } from '../utility/promiseHelper';
import {
  Post,
  PostDetailQueryData,
} from '../../../queries/contentPage/postDetail';
import { LocationItem } from '../../../queries/fragments/project';
import { ProjectArticleData } from '../../../queries/projectPage/projectArticles';
import { RecommendedData } from '../domain/recommendation';
import { OtherPreviewData } from '../domain/otherPreview';
import { SeoMetaComposerData } from '../domain/seoMetaComposerData';
import { DDListingsResponse } from '../../../lib/ddListingTypes';
import { makeStore } from '../../../store';

export interface ContentPageFetchResult {
  post: Post;
  projectRelatedPostData?: ProjectArticleData;
  postOtherPreviewData?: OtherPreviewData;
  postRecommendedData?: RecommendedData | Record<string, never>;
  postWhatToReadData?: { posts: { items: Post[] } };
  projectnetProjectData: TransformedProjectNetDataType;
  projectNetProjectHref: string | null;
  seoMetaComposerData: SeoMetaComposerData;
  ddListingsData: DDListingsResponse | null;
}

// Business requirement: If post category is location insight then retrieve by main category (which is all subcategories of the main category)
const getPostCategoryParams = (postCategory: PostSubcategory) => {
  return postCategory === 'location_insight'
    ? getSubcategories(getMainCategory(postCategory))
    : postCategory;
};

// Get district ids from project location for other preview posts
const getPropertyDistrictIds = (districts: LocationItem[]): number[] => {
  if (!districts || districts.length === 0) return [];

  const idsSet = new Set<number>();
  districts.forEach((district) => {
    const val = Number(district.value);
    if (!Number.isNaN(val)) idsSet.add(val);

    if (district.parent?.value) {
      const parentVal = Number(district.parent.value);
      if (!Number.isNaN(parentVal)) idsSet.add(parentVal);
    }
  });

  return Array.from(idsSet);
};

const fetchDDListingsForProject = async (
  projectNetPropertyId: number | string,
): Promise<DDListingsResponse | null> => {
  try {
    return await fetchDDListings(projectNetPropertyId);
  } catch (e) {
    logger.logError('fetchContentPageData', 'Error fetching DD listings', {
      e,
    });
    return null;
  }
};

const fetchSeoMetaComposerDataForProject = async (
  projectnetProject: ProjectData,
  postCategory: PostSubcategory,
  asPath: string,
): Promise<SeoMetaComposerData | null> => {
  try {
    const postCategoryParams = getPostCategoryParams(postCategory);
    const postCategoryNormalized = Array.isArray(postCategoryParams)
      ? postCategoryParams[0]
      : postCategoryParams;
    const seoMetaComposerRequestData = prepareParamsForSeoMetaComposerRequest(
      projectnetProject,
      postCategoryNormalized,
    );
    return await fetchSeoMetaComposerData(
      decodeURIComponent(asPath),
      seoMetaComposerRequestData,
    );
  } catch (e) {
    logger.logError('Error when fetching SEO Meta Composer data:', e);
    return null;
  }
};

interface ProjectNetFetchResult {
  projectnetProjectData: TransformedProjectNetDataType;
  projectNetProjectHref: string | null;
  ddListingsData: DDListingsResponse | null;
  seoMetaComposerData: SeoMetaComposerData | null;
  propertyType: string[];
}

const fetchProjectNetAndSeoData = async (
  projectNetPropertyId: number | string,
  propertyType: string | string[],
  postCategory: PostSubcategory,
  asPath: string,
): Promise<ProjectNetFetchResult> => {
  const normalizedPropertyType = Array.isArray(propertyType)
    ? propertyType
    : [propertyType];

  try {
    const projectnetProject: ProjectData =
      await fetchProjectData(projectNetPropertyId);
    const projectnetProjectData =
      transformProjectnetProjectData(projectnetProject);

    const [ddListingsData, seoMetaComposerData] = await Promise.all([
      fetchDDListingsForProject(projectNetPropertyId),
      fetchSeoMetaComposerDataForProject(
        projectnetProject,
        postCategory,
        asPath,
      ),
    ]);

    return {
      projectnetProjectData,
      projectNetProjectHref: getProjectNetProjectHref(
        normalizedPropertyType[0],
        projectNetPropertyId,
      ),
      ddListingsData,
      seoMetaComposerData,
      propertyType: normalizedPropertyType,
    };
  } catch (e) {
    logger.logError('Error when get post detail in content page component:', e);
    return {
      projectnetProjectData: null,
      projectNetProjectHref: null,
      ddListingsData: null,
      seoMetaComposerData: null,
      propertyType: normalizedPropertyType,
    };
  }
};

/**
 * Fetches all data needed to render a content page — the article itself, related
 * articles, other-preview posts, recommended videos, "read next" posts, ProjectNet
 * project data, DD listings, and SEO meta composer data — and dispatches the
 * corresponding Redux state.
 *
 * Shared between ContentPage and PreviewPage, since both render identical pages
 * and differ only in how articleId is derived from the URL and whether any
 * redirect happens afterward. This function never redirects and never reads
 * `res` : callers own that decision using `postData.post.status` /
 * `postData.post.link`, which remain on the Post object it returns.
 */
export const fetchContentPageData = async (
  articleId: string,
  apolloClient: ApolloClient<unknown>,
  store: ReturnType<typeof makeStore>,
  asPath: string,
): Promise<ContentPageFetchResult | null> => {
  let projectRelatedPostData: ProjectArticleData = null;
  let postRecommendedData: RecommendedData | Record<string, never> = {};
  let projectnetProjectData: TransformedProjectNetDataType = null;
  let projectNetProjectHref = null;
  let seoMetaComposerData = null;
  let ddListingsData: DDListingsResponse | null = null;

  store.dispatch(updateCurrentSourcePage(ADP.PAGE));

  if (!articleId || !Number.isFinite(Number.parseInt(articleId))) {
    return null;
  }

  const postData: PostDetailQueryData = await fetchPostDetails(apolloClient, {
    articleId: Number.parseInt(articleId),
  });

  if (!postData?.post) {
    return null;
  }

  const {
    categories,
    tags,
    id,
    title,
    postCategory,
    project,
    videoType,
    projectNetPropertyId,
  } = postData.post;

  const { propertyType } = postData.post;
  const articleTypeInGA = getArticleTypeInGA(postCategory, videoType);

  if (projectNetPropertyId && !isNull(projectNetPropertyId)) {
    const result = await fetchProjectNetAndSeoData(
      projectNetPropertyId,
      propertyType,
      postData.post.postCategory,
      asPath,
    );
    projectnetProjectData = result.projectnetProjectData;
    projectNetProjectHref = result.projectNetProjectHref;
    ddListingsData = result.ddListingsData;
    seoMetaComposerData = result.seoMetaComposerData;
  }

  store.dispatch(
    updateCurrentArticle({
      categories,
      tags,
      id,
      title,
      postCategory,
      articleTypeInGA,
    }),
  );

  if (!isNull(projectNetPropertyId) || !isNull(project.id)) {
    const {
      id: propertyId,
      name,
      developer,
      location,
      transportations,
      priceTypes,
    } = project;
    const projectPropertyType = !isNull(project.propertyType)
      ? isArray(project.propertyType)
        ? project.propertyType.join(',')
        : toString(project.propertyType)
      : '';
    store.dispatch(
      updateCurrentProject({
        id: propertyId,
        title: name,
        developer,
        propertyType: projectPropertyType,
        location,
        transportations,
        priceTypes,
      }),
    );

    try {
      projectRelatedPostData = await fetchProjectArticles(apolloClient, {
        projectId: project.id,
        projectNetPropertyId: projectNetPropertyId,
        excludeId: Number.parseInt(articleId),
      });
    } catch (e) {
      logger.logError(
        'Error when get post detail in content page component:',
        e,
      );
    }
  }

  // Use property type from projectnet data if available and non-empty, otherwise fall back to post data property type
  const propertyTypesForOtherPreview = projectnetProjectData?.propertyType
    ?.length
    ? projectnetProjectData.propertyType
    : postData.post.propertyType;

  const [otherPreviewPosts, recommendedVideos, readNextPosts] =
    await Promise.allSettled([
      fetchOtherPreviewPosts(apolloClient, {
        postId: Number.parseInt(articleId),
        propertyType: propertyTypesForOtherPreview,
        postCategory: getPostCategoryParams(postData.post.postCategory),
        locationDistrictId: getPropertyDistrictIds(
          project?.location?.district || [],
        ),
      }),
      fetchRecommendedVideos(apolloClient, {
        id: Number.parseInt(articleId),
      }),
      fetchReadNextPosts(apolloClient, {
        postId: Number.parseInt(articleId),
        offset: 3,
        perPage: 5,
      }),
    ]);
  const postOtherPreviewData = resolved(otherPreviewPosts);
  postRecommendedData = resolved(recommendedVideos);
  const postWhatToReadData = resolved(readNextPosts);

  return {
    post: postData.post,
    projectRelatedPostData,
    postOtherPreviewData,
    postRecommendedData,
    postWhatToReadData,
    projectnetProjectData,
    projectNetProjectHref,
    seoMetaComposerData,
    ddListingsData,
  };
};
